/* eslint-disable complexity */
import React, {FC} from 'react';

import {MediaQuery, RadioButton} from '@optimaxdev/design-system';
import classNames from 'classnames';
import AnimateHeight from 'react-animate-height';
import {connect} from 'react-redux';
import {useMediaQuery} from 'react-responsive';
import Element from 'react-scroll/modules/components/Element';

import {useFeature} from 'components/feature';
import {ApplicationStoreType} from 'desktop/store';

import {PAYMENTS} from '../../constants/paymentStep';
import {changePaymentMethod} from '../../store/reducers/paymentStep/paymentMethods/paymentMethods';

import {getPaymentMethodSaveLoading} from '../../store/selectors/paymentStep/paymentMethodSave/paymentMethodSave';
import {getAmazonV2Confirmed} from '../../store/selectors/paymentStep/paymentStep';

import {getIsPrimerCreditCardLoading} from '../../store/selectors/primer/primer';

import s from './paymentWrapper.scss';

export type PropsType = {
    code: string;
    changePayment: (code: PAYMENTS) => void;
    isSelected: boolean;
    title: React.ReactNode;
    children: React.ReactNode;
    rightSideIcon?: React.ReactNode;
    className?: string;
    isAmazonV2Confirmed: boolean;
    isPrimerCreditCardLoading: boolean;
    isPaymentSaveLoading: boolean;
    destroyOnHide?: boolean;
};

/**
 * dummy function to prevent event bubbling
 */
const doNothing = () => {};

/**
 * PaymentWrapper for checkout
 *
 * @constructor
 */
export const PaymentWrapper: FC<PropsType> = ({
    changePayment,
    children,
    className,
    code,
    destroyOnHide = false,
    isAmazonV2Confirmed,
    isPaymentSaveLoading,
    isPrimerCreditCardLoading,
    isSelected,
    rightSideIcon,
    title,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const isSmallScreen = useMediaQuery(MediaQuery.sm);
    const isDisabled = isPrimerCreditCardLoading || isPaymentSaveLoading || isAmazonV2Confirmed;

    const handleClick = () => changePayment(code as PAYMENTS);

    return (
        <div
            className={classNames(
                {
                    [s.activeMethod]: isSelected && isFacelift,
                    [s.inactiveMethod]: !isSelected,
                    [s.wrapper]: !isFacelift,
                    [s.wrapperFaceLift]: isFacelift,
                },
                className,
            )}
        >
            <div className={classNames(s.titleWrap, {[s.titleFaceLift]: isFacelift})}>
                <button
                    // when payment/save, saveOrder is on creation process, don't change payment
                    disabled={isDisabled}
                    onClick={handleClick}
                    type="button"
                >
                    <div className={s.block}>
                        <RadioButton
                            isChecked={isSelected}
                            isDisabled={isDisabled}
                            onChange={doNothing}
                            size={isSmallScreen || isFacelift ? 'medium' : 'small'}
                            value={code}
                        />{' '}
                        <div className={classNames(s.title, {[s.titleFacelift]: isFacelift})}>
                            {title}
                        </div>
                    </div>
                </button>
                {rightSideIcon}
            </div>
            <Element name={code}>
                <AnimateHeight duration={300} height={isSelected ? 'auto' : 0}>
                    {isSelected || !destroyOnHide ? (
                        <div
                            className={classNames(s[`contentWrapper--${code}`], {
                                [s.contentWrapperFacelift]: isFacelift,
                                [s[`contentWrapperFacelift--${code}`]]: isFacelift,
                            })}
                        >
                            {children}
                        </div>
                    ) : null}
                </AnimateHeight>
            </Element>
        </div>
    );
};

export const PaymentWrapperConnected = connect(
    (store: ApplicationStoreType) => ({
        isAmazonV2Confirmed: getAmazonV2Confirmed(store),
        isPaymentSaveLoading: getPaymentMethodSaveLoading(store),
        isPrimerCreditCardLoading: getIsPrimerCreditCardLoading(store),
    }),
    {
        changePayment: changePaymentMethod,
    },
)(PaymentWrapper);
